import { useEffect, useMemo, useState } from "react";
import { NIL, v4 as uuidv4 } from "uuid";
import { useLocation, useParams } from "react-router-dom";
import { convertFromHTML } from "draft-js";

import { useNotification } from "../../../context/NotificationContext";
import useApiToken from "../../../hooks/useApiToken";
import CreateRoleStatus from "./CreateRoleStatus";
import CreateProcessAutomation from "./CreateProcessAutomation";
import {
  areArraysEqual,
  areObjectsEqual,
  RoleStatusType,
} from "../../../Helpers";
import {
  AutoAdvanceCondition,
  Role,
  RoleStatus,
  RoleWorkflow,
  AssessmentTemplate,
  AssessmentSlot,
  UniversityBranch,
  University,
  RoleUniversity,
  RoleValidationConfig,
  Assessment,
  AssessmentQuestionnaire,
  Questions,
  AssessmentQuestion,
  RoleType,
  ProjectJobDescription,

} from "../../../Models";
import TextEditor from "../../TextEditor/TextEditor";
import ConfirmModal from "../../Shared/ConfirmModal";
import { TextEditorFields } from "../../Shared/TextEditorFields";
import { InputField } from "../../Shared/InputFields";
import { clearLocalStorageExceptDark } from "../../TextEditor/textEditorConfig";
import RankingType from "./RankingType";
import ToggleSwitch from "../../Shared/ToggleSwitch";
import AllWorkflowsDropDown from "./AllWorkflowsDropDown";
import parse from "html-react-parser";
import {
  ArrowPathIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
import DepartmentDropdown from "./DepartmentDropdown";
import CreateAssessmentSlots from "./CreateAssessmentSlots";
import MultiDomainInput from "./MultiDomainInput";

import {
  addAssessmentSlots,
  CreateRole,
  CreateRoleType,
  DeleteAutoAdvanceCondition,
  DeleteRoleType,
  getAssessmentSlotsByRole,
  getAssessmentVersions,
  GetRoleTypes,
  GetRoleValidationConfig,
  PostAutoAdvanceCondition,
  PostRoleStatus,
  PutUpdateRole,
  UpdateRoleQuestionShowInCandidateList,
  UpdateRoleStatusFull as UpdateRoleStatus,
  UpsertRoleValidationConfig
} from "../../../api/roles.api";
import { AddRoleQuestion, DeleteRoleQuestion, GetAllQuestionForRole } from "../../../api/roles.api";
import { getQuestionOptionsById } from "../../../api/assessmentquestion.api";
import {
  DEFAULT_GOOD_THRESHOLD,
  DEFAULT_RELEVANT_THRESHOLD,
  DEFAULT_TEXT_QUESTION_WEIGHT,
  GetRoleApplicationScoring,
  RoleOptionWeight,
  RoleTextQuestionWeight,
  SaveRoleApplicationScoring,
  isOptionsRoleQuestion,
  isTextRoleQuestion,
} from "../../../api/roleApplicationScoring.api";
import RoleQuestionWeightsEditor, { RoleQuestionOption } from "./RoleQuestionWeightsEditor";
import { getRankType } from "../../../api/ranking.api";
import { GetConfirmedUnis, getUniversityBranches, getAllRoleQuestions } from "../../../api/masterdata.api";
import { getRoleWorkflows, getWorkFlowStatuses } from "../../../api/roleworkflow.api";
import { fetchAssessmentTemplates } from "../../../api/assessmenttemplate.api";
import {
  GetJobDescriptions,
  AttachJobDescriptionToRole,
  DetachJobDescriptionFromRole,
} from "../../../api/jobdescription.api";
// import { getAssessmentVersions } from "../../../api/assessmentquestionnaires.api";

export type recruitmentProcess = {
  id: string;
  title: string;
  email: string;
  order: number;
  passedEmail: string;
  submittedEmail: string;
  rejectionEmail: string;
  assessmentTemplateId?: string | null;
  singleAssesmentId?: string | null;
  singleAssessment?: AssessmentQuestionnaire | null
  isTestSlotsAvailable?: boolean | null;
  roleStatusTypeId?: string;
};
export type AssessmentCondition = {
  id: string;
  name: string;
};

const defaultRankType = {
  id: NIL,
  name: "Overall",
};

const getProcessAndAutoAdvanceCondition = (
  roleStatuses: RoleStatus[] | undefined
) => {
  const orderedRoleStatus = roleStatuses?.sort((a, b) => a.order - b.order);
  const autoAdvanceConditions: AutoAdvanceCondition[] = [];

  const processesForRole: recruitmentProcess[] = [];
  orderedRoleStatus?.forEach((rs) => {
    rs.autoAdvanceConditions?.forEach((aac) => {
      autoAdvanceConditions.push(aac);
    });
    processesForRole.push({
      id: rs.id,
      title: rs.name,
      email: rs.email,
      assessmentTemplateId: rs.assessmentTemplateId,
      order: rs.order,
      passedEmail: rs.passedEmail,
      submittedEmail: rs.submittedEmail,
      rejectionEmail: rs.rejectionEmail,
      isTestSlotsAvailable: rs.isTestSlotsAvailable,
      roleStatusTypeId: rs.roleStatusTypeId,
    });
  });
  return { autoAdvanceConditions, processesForRole };
};

const dedupeQuestions = (questions: Questions[]) => {
  const seen = new Set<string>();
  return (questions ?? []).filter((question) => {
    if (!question?.id || seen.has(question.id)) return false;
    seen.add(question.id);
    return true;
  });
};

const stripHtmlTags = (value: string) =>
  value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeSearchValue = (value: string) =>
  stripHtmlTags(value).toLowerCase();

const QUESTION_RESULTS_PAGE_SIZE = 50;

const normalizeSlotsForComparison = (slots: AssessmentSlot[]) =>
  [...slots]
    .map((slot) => {
      const startTime = new Date(slot.startTime);
      const endTime = new Date(slot.endTime);

      return {
        id: slot.id,
        roleStatusId: slot.roleStatusId,
        assessmentQuestionnaireId: slot.assessmentQuestionnaire?.id || slot.assessmentQuestionnaireId || "",
        individualAssessmentId: slot.individualAssessmentId || "",
        startTime: isNaN(startTime.getTime()) ? "" : startTime.toISOString(),
        endTime: isNaN(endTime.getTime()) ? "" : endTime.toISOString(),
        isActive: !!slot.isActive,
      };
    })
    .sort((a, b) => a.id.localeCompare(b.id));

// Order-independent fingerprint of the scoring inputs, used only to detect "has this changed".
// Blank weights are dropped and numbers normalised so "5" and "5.0" don't register as an edit.
const buildScoringSnapshot = (
  weights: Record<string, string>,
  relevant: string,
  good: string,
  textWeights: Record<string, string> = {}
): string => {
  const normalise = (m: Record<string, string>) =>
    Object.entries(m)
      .filter(([, v]) => v.trim() !== "")
      .map(([k, v]) => [k, Number(v)])
      .sort(([a], [b]) => String(a).localeCompare(String(b)));
  return JSON.stringify({
    w: normalise(weights),
    t: normalise(textWeights),
    r: Number(relevant),
    g: Number(good),
  });
};

export default function CreateRoleForm({
  role,
  roleStatuses,
}: {
  role: Role | undefined;
  roleStatuses: RoleStatus[] | undefined;
}) {
  const [title, setTitle] = useState("");
  const [employmentType, setEmploymentType] = useState("");
  const [location, setLocation] = useState("");
  const [salary, setSalary] = useState("");
  const [salaryInNumber, setSalaryInNumber] = useState("");
  const [partTimeSalary, setPartTimeSalary] = useState("");
  const [fteSalary, setFteSalary] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [offerEmail, setOfferEmail] = useState("");
  const [rejectionEmail, setRejectionEmail] = useState("");
  const [assessmentLink, setAssessmentLink] = useState("");
  const { success, error: notifyError } = useNotification();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [processes, setProcesses] = useState<recruitmentProcess[]>([]);
  const [selectedRankType, setSelectedRankType] = useState(defaultRankType);
  const [rankTypes, setRankTypes] = useState<{ id: string; name: string }[]>(
    []
  );
  const [processAutomations, setProcessAutomations] = useState<
    AutoAdvanceCondition[]
  >([]);

  const [textEditorType, setTextEditorType] = useState("");
  const [showTextEditor, setShowTextEditor] = useState(false);
  const [recruitmentProcessId, setRecruitmentProcessId] = useState("");
  const roleId = useParams();
  const [assessments, setAssessments] = useState<AssessmentTemplate[]>([]);
  const [isStudentRole, setIsStudentRole] = useState(false);

  const [universityEmailDomain, setUniversityEmailDomain] =
    useState<string>("");
  const [allUniversityBranches, setAllUniversityBranches] = useState<
    UniversityBranch[]
  >([]);
  const [allowedUniversityBranches, setAllowedUniversityBranches] = useState<
    UniversityBranch[]
  >([]);
  const [showBranches, setShowBranches] = useState<boolean>(false);
  const [uniSearchTerm, setUniSearchTerm] = useState("");
  const [questionSearchTerm, setQuestionSearchTerm] = useState("");
  const [universityResults, setUniversityResults] = useState<University[]>([]);
  const [questionResults, setQuestionResults] = useState<Questions[]>([]);
  // Replace selectedUniversity with selectedUniversities array
  const [selectedUniversities, setSelectedUniversities] = useState<
    University[]
  >([]);
  const [selectedQuestions, setSelectedQuestions] = useState<Questions[]>([])

  // Application-question scoring. Options are loaded per selected question; weights are keyed
  // by option (answer) id and kept as the raw typed string so a blank field means "no weight"
  // rather than 0. Saved through its own recruiter-only endpoint after the questions are mapped.
  const [roleQuestionOptions, setRoleQuestionOptions] = useState<Record<string, RoleQuestionOption[]>>({});
  const [optionWeights, setOptionWeights] = useState<Record<string, string>>({});
  // questionId -> points for a free-text question, only where the recruiter (or a previous save)
  // set one. Questions missing here show DEFAULT_TEXT_QUESTION_WEIGHT; "" means "not scored".
  const [textWeights, setTextWeights] = useState<Record<string, string>>({});
  const [relevantThreshold, setRelevantThreshold] = useState(String(DEFAULT_RELEVANT_THRESHOLD));
  const [goodThreshold, setGoodThreshold] = useState(String(DEFAULT_GOOD_THRESHOLD));
  // What was loaded for an existing role, so isFormChanged can enable Update when only the
  // weights or cut-offs change. Null until loaded, which counts as "unchanged".
  const [initialScoringSnapshot, setInitialScoringSnapshot] = useState<string | null>(null);
  const [initialSelectedQuestions, setInitialSelectedQuestions] = useState<Questions[]>([]);
  const [initialSelectedQuestionIds, setInitialSelectedQuestionIds] = useState<string[]>([]);
  const [jdSearchTerm, setJdSearchTerm] = useState("");
  const [allJobDescriptions, setAllJobDescriptions] = useState<ProjectJobDescription[]>([]);
  const [selectedJobDescriptions, setSelectedJobDescriptions] = useState<ProjectJobDescription[]>([]);
  const [initialSelectedJobDescriptionIds, setInitialSelectedJobDescriptionIds] = useState<string[]>([]);
  const [loadingUnis, setLoadingUnis] = useState(false);
  const [isOnCampus, setIsOnCampus] = useState(false);
  const [hasQuestionairre, setHasQuestionairre] = useState(false);
  const [loadingQues, setLoadingQues] = useState(false);
  const [domainError, setDomainError] = useState<string>("");
  const [roleWorkflows, setRoleWorkflows] = useState<RoleWorkflow[]>([]);
  const [selectedWorkFlow, setSelectedWorkFlow] = useState<RoleWorkflow | null>(
    null
  );
  const [inputType, setInputType] = useState<string>("");
  const [department, setDepartment] = useState<any>();
  const [openDate, setOpenDate] = useState<Date | null>(null);
  const [closeDate, setCloseDate] = useState<Date | null>(null);
  const newRoleId = uuidv4();
  const token = useApiToken();
  const [slots, setSlots] = useState<AssessmentSlot[]>([]);
  const [initialSlots, setInitialSlots] = useState<AssessmentSlot[]>([]);
  const [errorMessageToolTip, setErrorMessageToolTip] = useState<string>("");
  // const [limitBranches, setLimitBranches] = useState(false);
  const [allowedBranch, setAllowedBranch] = useState(false);
  const url = useLocation();

  const [roleTypes, setRoleTypes] = useState<RoleType[]>([]);
  const [selectedRoleTypeId, setSelectedRoleTypeId] = useState<string>("");
  const [newRoleTypeName, setNewRoleTypeName] = useState<string>("");
  const [roleTypeMessage, setRoleTypeMessage] = useState<string>("");

  const isEditRole = url.pathname.includes("edit");
  const [selectedProcess, setSelectedProcess] =
    useState<recruitmentProcess | null>(null);
  const [config, setConfig] = useState<RoleValidationConfig | null>(null);
  const [assessmentVersions, setAssessmentVersions] = useState<
    { id: number; name: string }[]
  >([
    { id: 1, name: "Version 1 - Batch Submission" },
    { id: 2, name: "Version 2 - Sequential Submission" },
  ]);
  const [selectedAssessmentVersion, setSelectedAssessmentVersion] =
    useState<number>(1);

  const formFields = [
    {
      id: 1,
      label: "Job title",
      name: "jobTitle",
      inputId: "jobTitle",
      placeholder: "e.g. Junior Software Engineer",
      autoComplete: "jobTitle",
      value: title,
      setValue: setTitle,
    },
    {
      id: 2,
      label: "Employment type",
      name: "employmentType",
      inputId: "employmentType",
      placeholder: "e.g. Full time, Part time, Internship",
      autoComplete: "employmentType",
      value: employmentType,
      setValue: setEmploymentType,
    },
    {
      id: 3,
      label: "Location",
      name: "location",
      inputId: "location",
      placeholder: "e.g. Remote",
      autoComplete: "location",
      value: location,
      setValue: setLocation,
    },
    {
      id: 4,
      label: "Salary",
      name: "salary",
      inputId: "salary",
      placeholder: "e.g. US$950-US$1,300 per month",
      autoComplete: "salary",
      value: salary,
      setValue: setSalary,
    },
    {
      id: 5,
      label: "Salary (Number)",
      name: "salaryInNumber",
      inputId: "salaryInNumber",
      placeholder: "e.g. 50000",
      autoComplete: "salaryInNumber",
      type: "number",
      value: salaryInNumber,
      setValue: setSalaryInNumber,
    },
    {
      id: 6,
      label: "Part Time Salary (Number)",
      name: "partTimeSalary",
      inputId: "partTimeSalary",
      placeholder: "e.g. 25000",
      autoComplete: "partTimeSalary",
      type: "number",
      value: partTimeSalary,
      setValue: setPartTimeSalary,
    },
    {
      id: 7,
      label: "FTE Salary (Number)",
      name: "fteSalary",
      inputId: "fteSalary",
      placeholder: "e.g. 1000",
      autoComplete: "fteSalary",
      type: "number",
      value: fteSalary,
      setValue: setFteSalary,
    },
  ];

  const textEditorFields = [
    {
      id: 1,
      label: "Job description",
      state: jobDescription,
      setTextEditorType,
      setShowTextEditor,
    },
    {
      id: 3,
      label: "Offer email",
      state: offerEmail,
      setTextEditorType,
      setShowTextEditor,
    },
    {
      id: 4,
      label: "Rejection email",
      state: rejectionEmail,
      setTextEditorType,
      setShowTextEditor,
    },
  ];
  const { processesForRole, autoAdvanceConditions } = useMemo(() => {
    return getProcessAndAutoAdvanceCondition(roleStatuses);
  }, [roleStatuses]);
  const uniqueSelectedQuestions = useMemo(
    () => dedupeQuestions(selectedQuestions),
    [selectedQuestions]
  );

  // Load options for any newly selected options-question. Already-loaded questions are skipped,
  // so ticking questions on and off doesn't refetch.
  useEffect(() => {
    const missing = uniqueSelectedQuestions.filter(
      (q) => isOptionsRoleQuestion(q.tags) && roleQuestionOptions[q.id] === undefined
    );
    if (missing.length === 0) return;

    let cancelled = false;
    Promise.all(
      missing.map(async (q) => {
        try {
          const opts = await getQuestionOptionsById(q.id);
          const list: RoleQuestionOption[] = Array.isArray(opts)
            ? opts.map((o: { id: string; text: string }) => ({ id: o.id, text: o.text }))
            : [];
          return [q.id, list] as const;
        } catch {
          return [q.id, [] as RoleQuestionOption[]] as const;
        }
      })
    ).then((entries) => {
      if (cancelled) return;
      setRoleQuestionOptions((prev) => {
        const next = { ...prev };
        entries.forEach(([id, list]) => {
          next[id] = list;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [uniqueSelectedQuestions, roleQuestionOptions]);

  // Edit mode: pull the role's saved weights and thresholds.
  useEffect(() => {
    if (!token || !role?.id) return;

    let cancelled = false;
    GetRoleApplicationScoring(role.id, token)
      .then((scoring) => {
        if (cancelled) return;
        const loaded: Record<string, string> = {};
        scoring.weights.forEach((w) => {
          loaded[w.answerId] = String(w.weight);
        });
        const loadedText: Record<string, string> = {};
        (scoring.textWeights ?? []).forEach((w) => {
          loadedText[w.questionId] = String(w.weight);
        });
        setOptionWeights(loaded);
        setTextWeights(loadedText);
        setRelevantThreshold(String(scoring.relevantThreshold));
        setGoodThreshold(String(scoring.goodThreshold));
        setInitialScoringSnapshot(
          buildScoringSnapshot(
            loaded,
            String(scoring.relevantThreshold),
            String(scoring.goodThreshold),
            loadedText
          )
        );
      })
      .catch((err) => console.error("Failed to load application scoring", err));

    return () => {
      cancelled = true;
    };
  }, [token, role?.id]);

  // What each selected free-text question is actually worth right now: the saved/typed value, or
  // the default when nothing has been set. This is what's shown, saved and compared.
  const effectiveTextWeights = useMemo(() => {
    const result: Record<string, string> = {};
    uniqueSelectedQuestions
      .filter((q) => isTextRoleQuestion(q.tags))
      .forEach((q) => {
        result[q.id] = textWeights[q.id] ?? String(DEFAULT_TEXT_QUESTION_WEIGHT);
      });
    return result;
  }, [uniqueSelectedQuestions, textWeights]);

  // A text question still on its default (never saved) counts as a change, so Update is enabled
  // and saving stores the default instead of leaving that question unscored.
  const isScoringChanged =
    initialScoringSnapshot !== null &&
    buildScoringSnapshot(optionWeights, relevantThreshold, goodThreshold, effectiveTextWeights) !==
      initialScoringSnapshot;

  const thresholdError = useMemo(() => {
    const relevant = Number(relevantThreshold);
    const good = Number(goodThreshold);
    if (relevantThreshold.trim() === "" || goodThreshold.trim() === "") {
      return "Both cut-offs are required.";
    }
    if (!Number.isFinite(relevant) || !Number.isFinite(good)) return "Cut-offs must be numbers.";
    if (relevant < 0 || good > 100) return "Cut-offs must be between 0 and 100.";
    if (relevant > good) return '"Relevant from" must not be higher than "Good from".';
    return null;
  }, [relevantThreshold, goodThreshold]);

  const weightError = useMemo(() => {
    const bad = [...Object.values(optionWeights), ...Object.values(effectiveTextWeights)].some((raw) => {
      if (raw.trim() === "") return false;
      const n = Number(raw);
      return !Number.isFinite(n) || n < 0 || n > 1000;
    });
    return bad ? "Weights must be numbers between 0 and 1000." : null;
  }, [optionWeights, effectiveTextWeights]);

  // Sends only weights for options of questions that are still selected and still options-type,
  // so a question removed from the role takes its weights with it.
  const saveApplicationScoring = async (targetRoleId: string) => {
    if (!token) return;

    const weights: RoleOptionWeight[] = [];
    uniqueSelectedQuestions
      .filter((q) => isOptionsRoleQuestion(q.tags))
      .forEach((q) => {
        (roleQuestionOptions[q.id] ?? []).forEach((opt) => {
          const raw = optionWeights[opt.id];
          if (raw === undefined || raw.trim() === "") return;
          weights.push({ questionId: q.id, answerId: opt.id, weight: Number(raw) });
        });
      });

    const textWeightList: RoleTextQuestionWeight[] = Object.entries(effectiveTextWeights)
      .filter(([, raw]) => raw.trim() !== "")
      .map(([questionId, raw]) => ({ questionId, weight: Number(raw) }));

    try {
      await SaveRoleApplicationScoring(
        targetRoleId,
        {
          relevantThreshold: Number(relevantThreshold),
          goodThreshold: Number(goodThreshold),
          weights,
          textWeights: textWeightList,
        },
        token
      );
    } catch (err: any) {
      // The role and its questions are already saved by this point, so this is reported rather
      // than treated as a failed save.
      notifyError("Scoring not saved", err?.message || "The answer weights could not be saved.");
    }
  };
  const [visibleQuestionResultsCount, setVisibleQuestionResultsCount] =
    useState<number>(QUESTION_RESULTS_PAGE_SIZE);
  const normalizedQuestionSearchTerm = useMemo(
    () => normalizeSearchValue(questionSearchTerm),
    [questionSearchTerm]
  );
  const filteredQuestionResults = useMemo(() => {
    if (!normalizedQuestionSearchTerm) return [];

    // API can return broad results; enforce exact client-side filtering here.
    return dedupeQuestions(questionResults).filter((question) => {
      const alreadySelected = uniqueSelectedQuestions.some(
        (selected) => selected.id === question.id
      );
      if (alreadySelected) return false;

      const questionText = normalizeSearchValue(question.text ?? "");
      return questionText.includes(normalizedQuestionSearchTerm);
    });
  }, [questionResults, uniqueSelectedQuestions, normalizedQuestionSearchTerm]);
  const visibleQuestionResults = useMemo(
    () => filteredQuestionResults.slice(0, visibleQuestionResultsCount),
    [filteredQuestionResults, visibleQuestionResultsCount]
  );
  const hasMoreQuestionResults =
    filteredQuestionResults.length > visibleQuestionResults.length;
  const shouldShowQuestionDropdown = questionSearchTerm.trim().length >= 2;

  useEffect(() => {
    setVisibleQuestionResultsCount(QUESTION_RESULTS_PAGE_SIZE);
  }, [normalizedQuestionSearchTerm]);

  useEffect(() => {
    console.log(" [CreateRoleForm] useEffect triggered - role:", role, "isEditRole:", isEditRole);
    if (role && isEditRole && token) {
      console.log(" [CreateRoleForm] Fetching assessment slots for role ID:", role.id);
      getAssessmentSlotsByRole(role.id, token)
        .then((data) => {
          console.log(" [CreateRoleForm] Raw assessment slots data received:", data);
          console.log(" [CreateRoleForm] Number of slots:", data?.length || 0);

          if (!data || !Array.isArray(data)) {
            console.error(" [CreateRoleForm] Invalid data format - expected array, got:", typeof data);
            setSlots([]);
            return;
          }

          const formattedSlots = data.map((slot: any) => {
            console.log(" [CreateRoleForm] Formatting slot:", slot);
            return {
              ...slot,
              startTime: new Date(slot.startTime),
              endTime: new Date(slot.endTime),
              isLocked: true,
              isActive: slot.isActive,
              roleStatusId: slot.roleStatus?.id || slot.roleStatusId,
              roleStatusName: slot.rolestatus?.name || slot.roleStatusName,
              assessmentQuestionnaireId: slot.assessmentQuestionnaire?.id || slot.assessmentQuestionnaireId,
            };
          });

          console.log("[CreateRoleForm] Formatted slots:", formattedSlots);
          console.log(" [CreateRoleForm] Setting slots state with", formattedSlots.length, "slots");
          setSlots(formattedSlots);
        })
        .catch((error) => {
          console.error(" [CreateRoleForm] Error fetching assessment slots:", error);
          console.error(" [CreateRoleForm] Error details:", error.message, error.stack);
          setSlots([]);
          setInitialSlots([]);
        });
    } else {
      console.log(" [CreateRoleForm] Skipping fetch - role:", role, "isEditRole:", isEditRole);
      setSlots([]);
      setInitialSlots([]);
    }
  }, [role, isEditRole, token]);
  useEffect(() => {
    if (!token) return;
    getRankType(token).then((data) => {
      data.unshift(defaultRankType);
      setRankTypes(data);
    });
  }, [token]);
  //Filling the form with role data
  useEffect(() => {
    if (!role) return;
    setTitle(role.title);
    setEmploymentType(role.employmentType);
    setLocation(role.location);
    setSalary(role.salary);
    setSalaryInNumber(role.salaryInNumber ? role.salaryInNumber.toString() : "");
    setPartTimeSalary(role.partTimeSalary ? role.partTimeSalary.toString() : "");
    setFteSalary(role.fteSalary ? role.fteSalary.toString() : "");
    setJobDescription(role.description);
    setIsStudentRole(role.isStudent);
    setIsOnCampus(role.isOnCampus);
    setHasQuestionairre(role.hasQuestionairre);
    setAllowedBranch(role.branchAllowed);
    setUniversityEmailDomain(role.universityEmailDomain || "");
    setAllowedUniversityBranches(role.allowedUniversityBranches || []);
    // Set assessment version - handle null values from database
    setSelectedAssessmentVersion(role.assessmentVersion ?? 1);
    console.log("Loaded role assessmentVersion from database:", role.assessmentVersion);
    let roleUniversities: University[] = [];
    role.roleUniversities?.forEach((ru) => {
      if (ru.university) roleUniversities.push(ru.university);
    });

    setSelectedUniversities(roleUniversities);

    setDepartment({ id: "3", name: role.department });
    if (role.roleTypeId) {
      setSelectedRoleTypeId(role.roleTypeId);
    }
    role.openDate && setOpenDate(new Date(role.openDate));
    role.closeDate && setCloseDate(new Date(role.closeDate));

    const ranking = rankTypes.find((rt) => rt.id === role.rankTypeId);
    if (ranking) setSelectedRankType(ranking);

    if (!roleStatuses) return;

    roleStatuses.forEach((rs) => {
      if (rs.name === RoleStatusType.hired) setOfferEmail(rs.email ?? "");
      if (rs.name === RoleStatusType.rejected)
        setRejectionEmail(rs.email ?? "");
    });

    //this code is for creating the new ids and mapping them correctly
    if (!isEditRole) {
      const processesForRoleWithNewId = processesForRole.map((pr) => {
        const newId = uuidv4();
        autoAdvanceConditions.forEach((aac) => {
          if (aac.autoAdvanceStatusId === pr.id)
            aac.autoAdvanceStatusId = newId;
          if (aac.startStatusId === pr.id) aac.startStatusId = newId;
        });
        return { ...pr, id: newId };
      });
      autoAdvanceConditions.forEach((aac) => {
        aac.id = uuidv4();
      });
      setProcesses(processesForRoleWithNewId);
    } else {
      setProcesses(processesForRole);
    }
    setProcessAutomations(autoAdvanceConditions);
  }, [
    role,
    roleStatuses,
    rankTypes,
    isEditRole,
    processesForRole,
    autoAdvanceConditions,
  ]);
  useEffect(() => {
    if (!token || !role) return;

    if (!role.hasQuestionairre) {
      setSelectedQuestions([]);
      setInitialSelectedQuestions([]);
      setInitialSelectedQuestionIds([]);
      return;
    }

    GetAllQuestionForRole(role.id, token).then((questions) => {
      const uniqueQuestions = dedupeQuestions(questions ?? []);
      const normalized = uniqueQuestions.map((q) => ({
        ...q,
        showInCandidateList: Boolean((q as any).showInCandidateList),
      }));
      setSelectedQuestions(normalized);
      setInitialSelectedQuestions(normalized);
      setInitialSelectedQuestionIds(uniqueQuestions.map((question) => question.id));
    });
  }, [token, role]);


  useEffect(() => {
    if (!hasQuestionairre) {

      // Questionnaire is disabled: clear in-memory selections to avoid stale UI state.
      // Reset version to default (1) instead of 0, so the dropdown stays valid
      // when the questionnaire is re-enabled.
      setSelectedAssessmentVersion(1);
      setSelectedQuestions([]);
      setInitialSelectedQuestions([]);
      setQuestionSearchTerm("");
      setQuestionResults([]);
      setVisibleQuestionResultsCount(QUESTION_RESULTS_PAGE_SIZE);
    }
  }, [hasQuestionairre]);
  useEffect(() => {
    fetchUniversityBranches();

  }, []);

  // Job descriptions are a small, unsearched list — fetch once and filter client-side.
  useEffect(() => {
    if (!token) return;
    GetJobDescriptions(token).then((res) => setAllJobDescriptions(res));
  }, [token]);

  useEffect(() => {
    if (!role || allJobDescriptions.length === 0) return;
    const attached = allJobDescriptions.filter((jd) =>
      jd.roleMappings.some((m) => m.roleId === role.id)
    );
    setSelectedJobDescriptions(attached);
    setInitialSelectedJobDescriptionIds(attached.map((jd) => jd.id));
  }, [role, allJobDescriptions]);

  useEffect(() => {
    if (!isEditRole || !url.pathname.includes("create")) {
      setUniversityEmailDomain("");
      setUniSearchTerm("");
      setQuestionSearchTerm("");
      setSelectedUniversities([]);
    }
  }, [isOnCampus, isEditRole, url.pathname]);

  const fetchUniversityBranches = async () => {
    const res = await getUniversityBranches(token || "");
    if (res) {
      setAllUniversityBranches(res);
    }
  };
  useEffect(() => {
    if (uniSearchTerm.length < 2) {
      setUniversityResults([]);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        setLoadingUnis(true);
        const res = await GetConfirmedUnis(uniSearchTerm);
        setUniversityResults(res || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingUnis(false);
      }
    }, 300); // debounce

    return () => clearTimeout(timeout);
  }, [uniSearchTerm]);

  // getAllRoleQuestions
  useEffect(() => {
    if (!token) {
      setQuestionResults([]);
      return;
    }

    if (questionSearchTerm.length < 2) {
      setQuestionResults([]);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        setLoadingQues(true);
        const res = await getAllRoleQuestions(token, questionSearchTerm);
        setQuestionResults(res || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingQues(false);
      }
    }, 300); // debounce

    return () => clearTimeout(timeout);
  }, [questionSearchTerm, token]);

  useEffect(() => {
    GetRoleTypes()
      .then((res) => setRoleTypes(res))
      .catch((err) => {
        console.error(err);
        setRoleTypeMessage("Failed to load role types.");
      });
  }, []);

  const handleAddRoleType = async () => {
    if (!token) return;
    const name = newRoleTypeName.trim();
    if (!name) {
      setRoleTypeMessage("Role type name is required.");
      return;
    }
    try {
      const created = await CreateRoleType(name, token);
      setRoleTypes((prev) => [...prev, created]);
      setSelectedRoleTypeId(created.id);
      setNewRoleTypeName("");
      setRoleTypeMessage("");
      success("Role Type Added", `"${created.name}" has been added successfully.`);
    } catch (err: any) {
      setRoleTypeMessage(err.message || "Failed to create role type.");
      notifyError("Failed", err.message || "Failed to create role type.");
    }
  };

  const handleDeleteRoleType = async () => {
    if (!token || !selectedRoleTypeId) return;
    try {
      const deletedName = roleTypes.find((rt) => rt.id === selectedRoleTypeId)?.name;
      await DeleteRoleType(selectedRoleTypeId, token);
      setRoleTypes((prev) =>
        prev.filter((rt) => rt.id !== selectedRoleTypeId)
      );
      setSelectedRoleTypeId("");
      setRoleTypeMessage("");
      setShowDeleteConfirm(false);
      success("Role Type Deleted", `"${deletedName}" has been deleted successfully.`);
    } catch (err: any) {
      setRoleTypeMessage(err.message || "Failed to delete role type.");
      setShowDeleteConfirm(false);
      notifyError("Failed", err.message || "Failed to delete role type.");
    }
  };

  const isFormValid = () => {
    console.log(" [isFormValid] Checking form validity");
    const slotsAreValid = validateSlots(slots, processes);
    const hasValidQuestions = hasQuestionairre ? uniqueSelectedQuestions.length > 0 : true;
    console.log("  - slotsAreValid:", slotsAreValid);
    console.log("  - title:", title.trim() !== "");
    console.log("  - employmentType:", employmentType.trim() !== "");
    console.log("  - location:", location.trim() !== "");
    console.log("  - salary:", salary.trim() !== "");
    console.log("  - jobDescription:", convertFromHTML(jobDescription).contentBlocks.length !== 0);
    console.log("  - offerEmail:", convertFromHTML(offerEmail).contentBlocks.length !== 0);
    console.log("  - rejectionEmail:", convertFromHTML(rejectionEmail).contentBlocks.length !== 0);
    console.log("  - processes.length > 0:", processes.length > 0);
    console.log("  - domainError:", domainError === "");

    // Only enforced when the questionnaire is on - otherwise the scoring editor isn't shown and
    // there's nothing the recruiter could fix.
    const scoringIsValid = !hasQuestionairre || (thresholdError === null && weightError === null);

    const isValid = (
      scoringIsValid &&
      title.trim() !== "" &&
      employmentType.trim() !== "" &&
      location.trim() !== "" &&
      salary.trim() !== "" &&
      convertFromHTML(jobDescription).contentBlocks.length !== 0 &&
      convertFromHTML(offerEmail).contentBlocks.length !== 0 &&
      convertFromHTML(rejectionEmail).contentBlocks.length !== 0 &&
      processes.length > 0 &&
      slotsAreValid &&
      domainError === "" &&
      selectedRoleTypeId.trim() !== "" &&
      hasValidQuestions
    );
    console.log("  - Final isValid result:", isValid);
    return isValid;
  };
  const isFormChanged = useMemo(() => {
    if (!role) return false;

    const automationIsSame = areArraysEqual(processAutomations, autoAdvanceConditions);
    const processIsSame = areArraysEqual(processesForRole, processes);
    const slotsAreSame =
      JSON.stringify(normalizeSlotsForComparison(initialSlots)) ===
      JSON.stringify(normalizeSlotsForComparison(slots));
    const currentSelectedQuestionIds = uniqueSelectedQuestions.map((question) => question.id).sort();
    const initialQuestionIds = [...initialSelectedQuestionIds].sort();
    const isQuestionMappingSame =
      currentSelectedQuestionIds.length === initialQuestionIds.length &&
      currentSelectedQuestionIds.every((questionId, index) => questionId === initialQuestionIds[index]);
    const isQuestionListVisibilitySame =
      uniqueSelectedQuestions.length === initialSelectedQuestions.length &&
      uniqueSelectedQuestions.every((currentQ) => {
        const initialQ = initialSelectedQuestions.find((q) => q.id === currentQ.id);
        if (!initialQ) return false;
        return Boolean((currentQ as any).showInCandidateList) === Boolean((initialQ as any).showInCandidateList);
      });
    const currentJobDescriptionIds = selectedJobDescriptions.map((jd) => jd.id).sort();
    const initialJobDescriptionIds = [...initialSelectedJobDescriptionIds].sort();
    const isJobDescriptionMappingSame =
      currentJobDescriptionIds.length === initialJobDescriptionIds.length &&
      currentJobDescriptionIds.every((id, index) => id === initialJobDescriptionIds[index]);

    return (
      title.trim() !== role.title ||
      employmentType.trim() !== role.employmentType ||
      location.trim() !== role.location ||
      salary.trim() !== role.salary ||
      salaryInNumber !== (role.salaryInNumber?.toString() ?? "") ||
      partTimeSalary !== (role.partTimeSalary?.toString() ?? "") ||
      fteSalary !== (role.fteSalary?.toString() ?? "") ||
      jobDescription.trim() !== role.description ||
      selectedRankType.id !== (role.rankTypeId ?? NIL) ||
      isStudentRole !== role.isStudent ||
      isOnCampus !== role.isOnCampus ||
      hasQuestionairre !== role.hasQuestionairre ||
      allowedBranch !== role.branchAllowed ||
      universityEmailDomain !== (role.universityEmailDomain ?? "") ||
      (department?.name ?? "") !== (role.department ?? "") ||
      selectedRoleTypeId !== (role.roleTypeId ?? "") ||
      !isQuestionMappingSame ||
      !isQuestionListVisibilitySame ||
      isScoringChanged ||
      !automationIsSame ||
      !processIsSame ||
      !slotsAreSame ||
      !isJobDescriptionMappingSame
    );
  }, [
    role,
    processAutomations,
    autoAdvanceConditions,
    processesForRole,
    processes,
    initialSlots,
    slots,
    title,
    employmentType,
    location,
    salary,
    salaryInNumber,
    partTimeSalary,
    fteSalary,
    jobDescription,
    selectedRankType.id,
    isStudentRole,
    isOnCampus,
    hasQuestionairre,
    allowedBranch,
    universityEmailDomain,
    department?.name,
    selectedRoleTypeId,
    uniqueSelectedQuestions,
    initialSelectedQuestions,
    initialSelectedQuestionIds,
    isScoringChanged,
    selectedJobDescriptions,
    initialSelectedJobDescriptionIds,
  ]);
  const [questionsToRemove, setQuestionsToRemove] = useState<AssessmentQuestion[]>([]);
  useEffect(() => {
    if (!token || !role) return;
    GetAllQuestionForRole(role.id, token).then((questions) => {
      const questionsInDb = questions;
      // Find questions that are in DB but not in selectedQuestions
      const questionsToRemoveList = questionsInDb.filter(
        (dbQuestion: Questions) =>
          !selectedQuestions.some(selected => selected.id === dbQuestion.id)
      );
      setQuestionsToRemove(questionsToRemoveList);
    });

  }, [token, role, selectedQuestions]);

  const handleSaveClick = (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    const formData: Role = {
      id: newRoleId,
      title: title.trim(),
      employmentType: employmentType.trim(),
      location: location.trim(),
      salary: salary.trim(),
      salaryInNumber: salaryInNumber ? Number(salaryInNumber) : null,
      partTimeSalary: partTimeSalary ? Number(partTimeSalary) : null,
      fteSalary: fteSalary ? Number(fteSalary) : null,
      description: jobDescription.trim(),
      isActive: false,
      iscancel: false,
      rankTypeId: selectedRankType.id,
      date: new Date().toISOString(),
      isStudent: isStudentRole,
      isOnCampus: isOnCampus,
      hasQuestionairre: hasQuestionairre,
      universityEmailDomain: universityEmailDomain,
      branchAllowed: allowedBranch,
      // universityId: selectedUniversity.id,
      allowedUniversityBranches: allowedBranch ? allowedUniversityBranches : [],
      allowedUniversities: selectedUniversities.map((s) => s.id),
      department: department.name ?? "",
      openDate,
      closeDate,
      assessmentVersion: selectedAssessmentVersion,
      roleTypeId: selectedRoleTypeId,
    };
    console.log("Creating role with assessmentVersion:", selectedAssessmentVersion);
    CreateRole(formData, token).then(async (res) => {
      setTitle("");
      setEmploymentType("");
      setLocation("");
      setSalary("");
      setJobDescription("");
      setOfferEmail("");
      setSelectedRankType(defaultRankType);
      setRejectionEmail("");
      setIsStudentRole(false);
      setIsOnCampus(false);
      setHasQuestionairre(false);
      setUniversityEmailDomain("");
      setAllowedUniversityBranches([]);
      setDepartment(undefined);
      setOpenDate(null);
      setCloseDate(null);

      const roleStatuses: RoleStatus[] = processes.map((process, index) => {
        var email = null;
        if (process.title === RoleStatusType.hired) {
          email = offerEmail;
        } else if (process.title === RoleStatusType.rejected) {
          email = rejectionEmail;
        } else {
          email = process.email;
        }
        let assessmentIdTemp = process.singleAssesmentId == undefined ? null : process.singleAssesmentId;
        let assessmentTemplateIdTemp = process.assessmentTemplateId === undefined ? null : process.assessmentTemplateId;
        return {
          id: process.id,
          name: process.title,
          email: email,
          roleId: newRoleId,
          order: index + 1,
          assessmentTemplateId: assessmentTemplateIdTemp,
          assessmentId: assessmentIdTemp,
          passedEmail: process.passedEmail,
          rejectionEmail: process.rejectionEmail,
          submittedEmail: process.submittedEmail,
          isTestSlotsAvailable: process.isTestSlotsAvailable,
          roleStatusTypeId: process.roleStatusTypeId,
        };
      });

      roleStatuses[roleStatuses.length - 1] = {
        ...roleStatuses[roleStatuses.length - 1],
        order: roleStatuses.length - 1,
      };


      Promise.all(
        roleStatuses.map(async (roleStatus) => {
          await PostRoleStatus(roleStatus, token);
        })
      ).then(() => {
        if (slots.length > 0) {
          return addAssessmentSlots(slots, token);
        }
      });
      Promise.all(
        processAutomations.map(async (pa) => {
          await PostAutoAdvanceCondition(pa, token);
        })
      );
      await Promise.all(uniqueSelectedQuestions.map(async (q) => {
        await AddRoleQuestion(newRoleId, q.id, token);
      }));

      await Promise.all(
        uniqueSelectedQuestions.map(async (q) => {
          await UpdateRoleQuestionShowInCandidateList(
            newRoleId,
            q.id,
            Boolean((q as any).showInCandidateList),
            token
          );
        })
      );

      // After the questions are mapped: the scoring endpoint rejects weights for questions that
      // aren't on the role yet.
      if (hasQuestionairre && uniqueSelectedQuestions.length > 0) {
        await saveApplicationScoring(newRoleId);
      }
      setOptionWeights({});
      setTextWeights({});
      setRelevantThreshold(String(DEFAULT_RELEVANT_THRESHOLD));
      setGoodThreshold(String(DEFAULT_GOOD_THRESHOLD));
      await Promise.all(
        selectedJobDescriptions.map(async (jd) => {
          await AttachJobDescriptionToRole(jd.id, newRoleId, token);
        })
      );

      if (role?.roleValidationConfig) {
        UpsertRoleValidationConfig(newRoleId, role?.roleValidationConfig, token);
      }

      success("Created Successfully", "Role has been created successfully");
      setProcessAutomations([]);
      // setProcesses([
      //   { id: uuidv4(), title: RoleStatusType.rejected, email: rejectionEmail },
      //   { id: uuidv4(), title: RoleStatusType.hired, email: offerEmail },
      // ]);
      clearLocalStorageExceptDark();
      setTitle("");
      setEmploymentType("");
      setLocation("");
      setSalary("");
      setSalaryInNumber("");
      setPartTimeSalary("");
      setFteSalary("");
      setAssessmentLink("");
      setJobDescription("");
      setOfferEmail("");
      setRejectionEmail("");
      setSelectedWorkFlow(null);
      setProcesses([]);
      setSelectedProcess(null);
      setSlots([]);
      setSelectedAssessmentVersion(1);
    });
  };
  const handleUpdateClick = async (e: React.FormEvent) => {
    if (!token || !role) return;
    e.preventDefault();
    if (!isFormChanged) return;

    const formData: Role = {
      id: role.id,
      title: title.trim(),
      employmentType: employmentType.trim(),
      location: location.trim(),
      salary: salary.trim(),
      salaryInNumber: salaryInNumber ? Number(salaryInNumber) : null,
      partTimeSalary: partTimeSalary ? Number(partTimeSalary) : null,
      fteSalary: fteSalary ? Number(fteSalary) : null,
      description: jobDescription.trim(),
      isActive: role.isActive,
      iscancel: role.iscancel,
      // use the current UI state value rather than the original role payload
      hasQuestionairre: hasQuestionairre,
      rankTypeId: selectedRankType.id,
      universityEmailDomain: universityEmailDomain,
      allowedUniversityBranches: allowedBranch ? allowedUniversityBranches : [],
      allowedUniversities: selectedUniversities.map((s) => s.id),
      branchAllowed: allowedBranch,
      // universityId: selectedUniversity.id,
      date: role.date,
      isStudent: isStudentRole,
      isOnCampus: isOnCampus,
      department: department.name,
      roleTypeId: selectedRoleTypeId || role.roleTypeId,
      openDate: openDate?.toISOString(),
      closeDate: closeDate?.toISOString(),
      assessmentVersion: selectedAssessmentVersion,
    };

    console.log("Updating role with assessmentVersion:", selectedAssessmentVersion);


    await PutUpdateRole(role.id, formData, token);
    const statusUpdatePromises = processes.map(async (p) => {
      const prev = processesForRole.find((pr) => pr.id === p.id);
      const update = roleStatuses?.find((rs) => rs.id === p.id);
      if (!update) return;

      const effectiveEmail =
        p.title === RoleStatusType.hired
          ? offerEmail
          : p.title === RoleStatusType.rejected
            ? rejectionEmail
            : p.email;

      const emailChanged = effectiveEmail !== update.email;
      const otherChanged = prev ? !areObjectsEqual(prev, p) : true;
      let assessmentIdChanged = p.singleAssesmentId == undefined ? null : p.singleAssesmentId;
      let assessmentTemplateIdChanged = p.assessmentTemplateId === undefined ? null : p.assessmentTemplateId;
      if (assessmentIdChanged != null) assessmentTemplateIdChanged = null;
      if (emailChanged || otherChanged) {
        return UpdateRoleStatus(p.id, token, {
          id: p.id,
          email: effectiveEmail,
          roleId: update.roleId,
          order: update.order,
          name: p.title,
          assessmentTemplateId: assessmentTemplateIdChanged,
          assessmentId: assessmentIdChanged,
          passedEmail: p.passedEmail,
          rejectionEmail: p.rejectionEmail,
          submittedEmail: p.submittedEmail,
          isTestSlotsAvailable: p.isTestSlotsAvailable,
          roleStatusTypeId: p.roleStatusTypeId ?? undefined,
        });
      }
    });
    await Promise.all(statusUpdatePromises);
    if (slots.length > 0) {
      await addAssessmentSlots(slots, token);
    }
    const existingQuestions = await GetAllQuestionForRole(role.id, token);
    const existingQuestionIds = new Set(
      (existingQuestions ?? []).map((q: { id: string }) => q.id)
    );
    const selectedQuestionIds = new Set(uniqueSelectedQuestions.map((q) => q.id));

    const questionsToAdd = uniqueSelectedQuestions.filter(
      (q) => !existingQuestionIds.has(q.id)
    );
    const questionsToDelete = (existingQuestions ?? []).filter(
      (q: { id: string }) => !selectedQuestionIds.has(q.id)
    );

    await Promise.all(
      questionsToAdd.map(async (q) => {
        await AddRoleQuestion(role.id, q.id, token);
      })
    );

    await Promise.all(
      questionsToDelete.map(async (q: { id: string }) => {
        await DeleteRoleQuestion(role.id, q.id, token);
      })
    );

    const initialJobDescriptionIdSet = new Set(initialSelectedJobDescriptionIds);
    const selectedJobDescriptionIdSet = new Set(selectedJobDescriptions.map((jd) => jd.id));

    const jobDescriptionsToAdd = selectedJobDescriptions.filter(
      (jd) => !initialJobDescriptionIdSet.has(jd.id)
    );
    const jobDescriptionIdsToRemove = initialSelectedJobDescriptionIds.filter(
      (id) => !selectedJobDescriptionIdSet.has(id)
    );

    await Promise.all(
      jobDescriptionsToAdd.map(async (jd) => {
        await AttachJobDescriptionToRole(jd.id, role.id, token);
      })
    );

    await Promise.all(
      jobDescriptionIdsToRemove.map(async (jdId) => {
        await DetachJobDescriptionFromRole(jdId, role.id, token);
      })
    );

    await Promise.all(
      uniqueSelectedQuestions.map(async (q) => {
        await UpdateRoleQuestionShowInCandidateList(
          role.id,
          q.id,
          Boolean((q as any).showInCandidateList),
          token
        );
      })
    );

    // Always saved on edit, even with no questions left: the save is replace-all and re-scores
    // every existing applicant, so removing the last weighted question correctly moves them all
    // back to Not scored instead of leaving categories calculated under rules that no longer exist.
    await saveApplicationScoring(role.id);
    setTextWeights((prev) => ({ ...prev, ...effectiveTextWeights }));
    setInitialScoringSnapshot(
      buildScoringSnapshot(optionWeights, relevantThreshold, goodThreshold, effectiveTextWeights)
    );

    const refreshedQuestions = await GetAllQuestionForRole(role.id, token);
    const refreshedUniqueQuestions = dedupeQuestions(refreshedQuestions ?? []);
    const normalizedRefreshed = refreshedUniqueQuestions.map((q) => ({
      ...q,
      showInCandidateList: Boolean((q as any).showInCandidateList),
    }));
    setSelectedQuestions(normalizedRefreshed);
    setInitialSelectedQuestions(normalizedRefreshed);
    setInitialSelectedQuestionIds(refreshedUniqueQuestions.map((question) => question.id));

    const processAutomationsToDelete = autoAdvanceConditions.filter(
      (aac) => !processAutomations.some((pa) => pa.id === aac.id)
    );
    const processAutomationsToAdd = processAutomations.filter(
      (pa) => !autoAdvanceConditions.some((aac) => aac.id === pa.id)
    );
    await Promise.all(
      processAutomationsToDelete.map(async (pa) => {
        await DeleteAutoAdvanceCondition(pa.id, token);
      })
    );
    await Promise.all(
      processAutomationsToAdd.map(async (pa) => {
        await PostAutoAdvanceCondition(pa, token);
      })
    );

    success("Updated Successfully", "Role has been updated successfully");
    setTimeout(() => {
      window.location.reload();
    }, 500);

    clearLocalStorageExceptDark();
  };

  const validateSlots = (
    slots: AssessmentSlot[],
    processes: recruitmentProcess[]
  ) => {
    console.log(" [validateSlots] Starting validation");
    console.log("  - Slots:", slots);
    console.log("  - Processes:", processes);


    const processesWithTestSlots = processes.filter(
      (p) => p.isTestSlotsAvailable
    );
    console.log("  - Processes with test slots:", processesWithTestSlots.length, processesWithTestSlots.map(p => ({ id: p.id, title: p.title })));

    var errorMessage = "";
    if (processesWithTestSlots.length === 0 && slots.length > 0) {
      errorMessage = "No test appointments available, but slots are provided.";
      console.log("[validateSlots] Validation failed: No test appointments but slots exist");
      if (errorMessageToolTip !== errorMessage) {
        setErrorMessageToolTip(errorMessage);
      }
      return false;
    }
    const slotsRoleStatusIds = new Set(slots.map((slot) => slot.roleStatusId));
    console.log("  - Slot roleStatusIds:", Array.from(slotsRoleStatusIds));

    // Only validate that slots exist if there are any slots at all
    // Don't require ALL processes to have slots - allow partial slot creation
    // The validation should only check that existing slots are valid
    if (slots.length === 0) {
      console.log("  - No slots to validate - validation passes (slots are optional)");
      if (errorMessageToolTip) {
        setErrorMessageToolTip("");
      }
      return true;
    }
    console.log("  - Slots exist, validating slot integrity (not requiring all processes to have slots)");

    for (const slot of slots) {
      console.log("  - Validating slot:", slot);
      const process = processes.find((p) => p.id === slot.roleStatusId);
      console.log("    - Found process:", process ? { id: process.id, title: process.title } : "NOT FOUND");

      if (!process) {
        errorMessage = `No matching recruitment process found for some slots.`;
        console.log(" [validateSlots] Validation failed: No matching process for slot");
        if (errorMessageToolTip !== errorMessage) {
          setErrorMessageToolTip(errorMessage);
        }
        return false;
      }
      //||  slot.assessmentQuestionnaire.assessmentTemplate?.id !==  process.assessmentTemplateId
      if (process.assessmentTemplateId) {
        console.log("    - Process has assessmentTemplateId:", process.assessmentTemplateId);
        console.log("    - Slot assessmentQuestionnaire:", slot.assessmentQuestionnaire);

        if (
          !slot.assessmentQuestionnaire
        ) {
          errorMessage = `Assessment questionnaire in created slots does not match with assigned template.`;
          console.log(" [validateSlots] Validation failed: Missing assessmentQuestionnaire");
          if (errorMessageToolTip !== errorMessage) {
            setErrorMessageToolTip(errorMessage);
          }
          return false;
        }
      }
    }
    if (errorMessageToolTip) {
      setErrorMessageToolTip("");
    }
    console.log(" [validateSlots] Validation passed!");
    return true;
  };

  const handleTextEditor = (type: string, inputType: string) => {
    setInputType(inputType);
    setTextEditorType(type);
    setShowTextEditor(true);
    window.scrollTo(0, 0);
  };

  const getDefaultStateValue = (textEditorType: string) => {
    const selectedProcess = processes.find(
      (process) => process.id === recruitmentProcessId
    );
    switch (textEditorType) {
      case "Job description":
        return jobDescription;
      case "Offer email":
        return offerEmail;
      case "Rejection email":
        return rejectionEmail;
      case recruitmentProcessId:
        switch (inputType) {
          case "emailBody":
            return selectedProcess?.email;
          case "submittedBody":
            return selectedProcess?.submittedEmail;
          case "rejectedBody":
            return selectedProcess?.rejectionEmail;
          case "passedBody":
            return selectedProcess?.passedEmail;
          default:
            setInputType("");
            return "";
        }
      default:
        setInputType("");
        return "";
    }
  };

  useEffect(() => {
    if (!token) return;
    getRoleWorkflows(token).then((res) => setRoleWorkflows(res));
    fetchAssessmentTemplates().then((res) => setAssessments(res));
    getAssessmentVersions().then((res) => setAssessmentVersions(res));
  }, [token]);

  useEffect(() => {
    if (selectedWorkFlow && token) {
      getWorkFlowStatuses(selectedWorkFlow?.id, token).then(
        (res: recruitmentProcess[]) => {
          let recProcess: recruitmentProcess[] = [];
          res.map((r) => {
            console.log("Loading workflow status for role:", r.title, "roleStatusTypeId:", r.roleStatusTypeId);
            recProcess.push({
              id: uuidv4(),
              title: r.title,
              email: r.email,
              order: r.order,
              assessmentTemplateId: r.assessmentTemplateId,
              passedEmail: r.passedEmail ?? "",
              rejectionEmail: r.rejectionEmail ?? "",
              submittedEmail: r.submittedEmail ?? "",
              isTestSlotsAvailable: r.isTestSlotsAvailable,
              roleStatusTypeId: r.roleStatusTypeId ?? undefined,
            });
          });
          setProcesses(recProcess);
        }
      );
    }
  }, [selectedWorkFlow, token]);

  return (
    <>
      {!showTextEditor ? (
        <form>
          <div className="space-y-12 sm:space-y-16 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ">
            <div>
              {!isEditRole && (
                <p className="mt-6 max-w-2xl text-sm leading-6 text-gray-600 dark:text-gray-300">
                  This jobs you create are not active by default.
                </p>
              )}
              <div>
                <div className="mt-4 space-y-8 border-b border-gray-900/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-gray-900/10 sm:border-t sm:pb-0 dark:border-gray-800  dark:sm:divide-gray-800">
                  <div className="flex sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                    <h3 className="text-sm font-medium leading-6 text-gray-900 dark:text-gray-400">
                      Department <span className="text-red-500"> *</span>
                    </h3>
                    <DepartmentDropdown
                      placeHolder="Select department"
                      dropDownValues={[
                        { id: "1", name: "IT" },
                        { id: "2", name: "Shipping" },
                      ]}
                      currentValue={department}
                      setCurrentValue={setDepartment}
                      query=""
                      setQuery={() => { }}
                      dropDownOnly
                    />
                  </div>
                  <div className="flex sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                    <h3 className="text-sm font-medium leading-6 text-gray-900 dark:text-gray-400">
                      Role Type <span className="text-red-500"> *</span>
                    </h3>
                    <div className="sm:col-span-2 space-y-2">
                      <select
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 bg-transparent dark:text-gray-200 dark:ring-gray-600 sm:text-sm sm:leading-6"
                        value={selectedRoleTypeId}
                        onChange={(e) => setSelectedRoleTypeId(e.target.value)}
                      >
                        <option value="">Select a role type</option>
                        {roleTypes.map((rt) => (
                          <option key={rt.id} value={rt.id}>
                            {rt.name}
                          </option>
                        ))}
                      </select>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          className="flex-1 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 bg-transparent dark:text-gray-200 dark:ring-gray-600 sm:text-sm sm:leading-6"
                          placeholder="Add new role type"
                          value={newRoleTypeName}
                          onChange={(e) => setNewRoleTypeName(e.target.value)}
                        />
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md bg-entntblue px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-entntorange"
                          onClick={handleAddRoleType}
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                          onClick={() => setShowDeleteConfirm(true)}
                          disabled={!selectedRoleTypeId}
                        >
                          Delete
                        </button>
                      </div>
                      {roleTypeMessage && (
                        <p className="text-xs text-red-500 mt-1">
                          {roleTypeMessage}
                        </p>
                      )}
                      <ConfirmModal
                        type="danger"
                        open={showDeleteConfirm}
                        setOpen={setShowDeleteConfirm}
                        title="Delete Role Type"
                        description="Are you sure you want to delete this role type? It must not be used by any existing roles."
                        confirmButtonTitle="Delete"
                        onConfirm={handleDeleteRoleType}
                      />
                    </div>
                  </div>
                  {formFields.map((field) => (
                    <InputField
                      key={field.id}
                      label={field.label}
                      name={field.name}
                      inputId={field.inputId}
                      placeholder={field.placeholder}
                      autoComplete={field.autoComplete}
                      value={field.value}
                      setValue={field.setValue}
                    />
                  ))}
                  <div className="flex sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 text-sm font-medium leading-6 text-gray-900 dark:text-gray-400">
                    <label htmlFor="student-role">Hiring students?</label>
                    <ToggleSwitch
                      id="student-role"
                      // label="Hiring students?"
                      checked={isStudentRole}
                      setState={setIsStudentRole}
                    />
                  </div>
                  <div className="flex sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 text-sm font-medium leading-6 text-gray-900 dark:text-gray-400">
                    <label htmlFor="questionnaire">Has Questionnaire?</label>
                    <ToggleSwitch
                      id="questionairre"
                      checked={hasQuestionairre}
                      setState={setHasQuestionairre}
                    />
                  </div>
                  {hasQuestionairre && (
                    <>
                      <div className="flex sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-3 text-sm font-medium leading-6 text-gray-900 dark:text-gray-400">
                        <label>Select Questions</label>
                        <div className="w-full">
                          <div className="relative">
                            <input
                              type="text"
                              value={questionSearchTerm}
                              onChange={(e) => {
                                setQuestionSearchTerm(e.target.value);
                              }}
                              placeholder="Search questions..."
                              className="w-full border border-gray-300 text-sm rounded-lg px-2 py-1 dark:bg-darkbg dark:text-gray-200 placeholder:text-sm"
                            />
                            {loadingQues && (
                              <span className="absolute right-2 top-2 text-xs text-gray-500">
                                Loading...
                              </span>
                            )}

                            {/* Dropdown results */}
                            {shouldShowQuestionDropdown && (
                              <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto overscroll-contain border rounded bg-white dark:bg-darkbg shadow-lg">
                                {!loadingQues && filteredQuestionResults.length === 0 && (
                                  <div className="px-3 py-2 text-xs text-gray-500">
                                    No matching questions found.
                                  </div>
                                )}

                                {visibleQuestionResults.map((question) => (
                                  <div
                                    key={question.id}
                                    onClick={() => {
                                      setSelectedQuestions((prev) =>
                                        dedupeQuestions([
                                          ...prev,
                                          {
                                            ...question,
                                            showInCandidateList: Boolean(
                                              (question as any).showInCandidateList
                                            ),
                                          },
                                        ])
                                      );
                                      setQuestionSearchTerm("");
                                      setQuestionResults([]);
                                      setVisibleQuestionResultsCount(
                                        QUESTION_RESULTS_PAGE_SIZE
                                      );
                                    }}
                                    className="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                                  >
                                    {parse(question.text)}
                                  </div>
                                ))}

                                {hasMoreQuestionResults && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setVisibleQuestionResultsCount(
                                        (prev) => prev + QUESTION_RESULTS_PAGE_SIZE
                                      )
                                    }
                                    className="w-full border-t px-3 py-2 text-left text-xs font-medium text-entntblue hover:bg-gray-50 dark:hover:bg-gray-800"
                                  >
                                    Show more ({filteredQuestionResults.length -
                                      visibleQuestionResults.length} remaining)
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          {uniqueSelectedQuestions.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {uniqueSelectedQuestions.map((question) => (
                                <div
                                  key={question.id}
                                  className="flex items-center bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-3 py-1 rounded-lg text-xs"
                                >
                                  <span>{parse(question.text)}</span>
                                  <label className="ml-3 flex items-center gap-1 text-[10px] select-none">
                                    <input
                                      type="checkbox"
                                      checked={Boolean((question as any).showInCandidateList)}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        setSelectedQuestions((prev) =>
                                          prev.map((q) =>
                                            q.id === question.id
                                              ? { ...q, showInCandidateList: checked }
                                              : q
                                          )
                                        );
                                      }}
                                      className="h-3 w-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                    />
                                    <span>Show in list</span>
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedQuestions((prev) =>
                                        prev.filter(
                                          (selected) => selected.id !== question.id
                                        )
                                      );
                                    }}
                                    className="ml-2 text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100 font-bold"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          <RoleQuestionWeightsEditor
                            questions={uniqueSelectedQuestions}
                            optionsByQuestion={roleQuestionOptions}
                            weights={optionWeights}
                            onWeightChange={(answerId, value) =>
                              setOptionWeights((prev) => ({ ...prev, [answerId]: value }))
                            }
                            textWeights={effectiveTextWeights}
                            onTextWeightChange={(questionId, value) =>
                              setTextWeights((prev) => ({ ...prev, [questionId]: value }))
                            }
                            relevantThreshold={relevantThreshold}
                            goodThreshold={goodThreshold}
                            onThresholdChange={(which, value) =>
                              which === "relevant"
                                ? setRelevantThreshold(value)
                                : setGoodThreshold(value)
                            }
                            thresholdError={thresholdError ?? weightError}
                          />
                        </div>
                      </div>

                    </>
                  )}
                  <div className="flex sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 text-sm font-medium leading-6 text-gray-900 dark:text-gray-400">
                    <label htmlFor="oncampus">
                      Partner institution hiring?
                    </label>

                    <ToggleSwitch
                      id="oncampus"
                      // label="Partner institution hiring?"
                      checked={isOnCampus}
                      setState={setIsOnCampus}
                    />
                  </div>

                  {/* Attach project-specific job descriptions — many-to-many with Role */}
                  <div className="flex sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-3 text-sm font-medium leading-6 text-gray-900 dark:text-gray-400">
                    <label>Attach Job Descriptions</label>
                    <div className="w-full">
                      <div className="relative">
                        <input
                          type="text"
                          value={jdSearchTerm}
                          onChange={(e) => {
                            setJdSearchTerm(e.target.value);
                          }}
                          placeholder="Search job descriptions..."
                          className="w-full border border-gray-300 text-sm rounded-lg px-2 py-1 dark:bg-darkbg dark:text-gray-200 placeholder:text-sm"
                        />

                        {jdSearchTerm.length > 0 && (
                          <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto border rounded bg-white dark:bg-darkbg shadow-lg">
                            {allJobDescriptions
                              .filter(
                                (jd) =>
                                  jd.title.toLowerCase().includes(jdSearchTerm.toLowerCase()) &&
                                  !selectedJobDescriptions.some((selected) => selected.id === jd.id)
                              )
                              .map((jd) => (
                                <div
                                  key={jd.id}
                                  onClick={() => {
                                    setSelectedJobDescriptions((prev) => [...prev, jd]);
                                    setJdSearchTerm("");
                                  }}
                                  className="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                  {jd.title}
                                  {!jd.isActive && (
                                    <span className="ml-2 text-xs text-red-500">(closed)</span>
                                  )}
                                </div>
                              ))}
                          </div>
                        )}
                      </div>

                      {selectedJobDescriptions.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {selectedJobDescriptions.map((jd) => (
                            <div
                              key={jd.id}
                              className="flex items-center bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-3 py-1 rounded-lg text-xs"
                            >
                              <span>{jd.title}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedJobDescriptions((prev) =>
                                    prev.filter((selected) => selected.id !== jd.id)
                                  );
                                }}
                                className="ml-2 text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100 font-bold"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {isOnCampus && (
                    <>
                      {/* Select Multiple Universities */}
                      <div className="flex sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-3 text-sm font-medium leading-6 text-gray-900 dark:text-gray-400">
                        <label>Select Universities</label>
                        <div className="w-full">
                          <div className="relative">
                            <input
                              type="text"
                              value={uniSearchTerm}
                              onChange={(e) => {
                                setUniSearchTerm(e.target.value);
                              }}
                              placeholder="Search universities..."
                              className="w-full border border-gray-300 text-sm rounded-lg px-2 py-1 dark:bg-darkbg dark:text-gray-200 placeholder:text-sm"
                            />
                            {loadingUnis && (
                              <span className="absolute right-2 top-2 text-xs text-gray-500">
                                Loading...
                              </span>
                            )}

                            {/* Dropdown results */}
                            {universityResults.length > 0 && uniSearchTerm && (
                              <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto border rounded bg-white dark:bg-darkbg shadow-lg">
                                {universityResults
                                  .filter(
                                    (uni) =>
                                      !selectedUniversities.some(
                                        (selected) => selected.id === uni.id
                                      )
                                  )
                                  .map((uni) => (
                                    <div
                                      key={uni.id}
                                      onClick={() => {
                                        setSelectedUniversities((prev) => [
                                          ...prev,
                                          uni,
                                        ]);
                                        setUniSearchTerm("");
                                        setUniversityResults([]);
                                      }}
                                      className="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                      {uni.name}
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>

                          {/* Selected Universities Badges */}
                          {selectedUniversities.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {selectedUniversities.map((uni) => (
                                <div
                                  key={uni.id}
                                  className="flex items-center bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-3 py-1 rounded-lg text-xs"
                                >
                                  <span>{uni.name}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedUniversities((prev) =>
                                        prev.filter(
                                          (selected) => selected.id !== uni.id
                                        )
                                      );
                                    }}
                                    className="ml-2 text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100 font-bold"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <MultiDomainInput
                        universityEmailDomain={universityEmailDomain}
                        setUniversityEmailDomain={setUniversityEmailDomain}
                        domainError={domainError}
                        setDomainError={setDomainError}
                      />
                      {/* <div className="flex sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-3 text-sm font-medium leading-6 text-gray-900 dark:text-gray-400">
                        <label htmlFor="university-domain">
                          Allowed Email Domain{" "}
                          <span className="text-gray-500">
                            (must start with @)
                          </span>
                        </label>
                        <input
                          id="university-domain"
                          type="text"
                          value={universityEmailDomain}
                          onChange={(e) => {
                            const value = e.target.value;
                            setUniversityEmailDomain(value);
                            if (value && !/^@.+/.test(value)) {
                              setDomainError("Domain must start with @");
                            } else {
                              setDomainError("");
                            }
                          }}
                          placeholder="@example.com"
                          className="border px-2 py-1 dark:bg-darkbg dark:text-gray-200 border-gray-300 text-sm rounded-lg"
                        />
                        {domainError && (
                          <span className="text-red-500 text-xs">
                            {domainError}
                          </span>
                        )}
                      </div> */}
                    </>
                  )}
                  <div>
                    <div className="flex sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-2 text-sm font-medium leading-6 text-gray-900 dark:text-gray-400">
                      <label htmlFor="allowedBranch">
                        Enable Branch Selection for Role
                      </label>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="allowedBranch"
                          checked={allowedBranch}
                          onChange={(e) => {
                            setAllowedBranch(e.target.checked);
                            setAllowedUniversityBranches(allUniversityBranches);
                          }}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        />
                        <span className="ml-2 text-sm text-gray-900 dark:text-gray-300">
                          Allow Branch Selection
                        </span>
                      </div>
                    </div>
                    {/* === Inner controls: Only show if allowedBranch is true === */}
                    {allowedBranch && (
                      <>
                        {/* Limit Branches toggle */}
                        {/* <div className="flex sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-2 sm:px-6 text-sm font-medium leading-6 text-gray-900 dark:text-gray-400">
                          <label htmlFor="limitBranches">Limit Branches</label>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="limitBranches"
                              checked={limitBranches}
                              onChange={(e) => {
                                setLimitBranches(e.target.checked);
                                setAllowedUniversityBranches([]);
                              }}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span className="ml-2 text-sm text-gray-900 dark:text-gray-300">
                              Enable branch filtering
                            </span>
                          </div>
                        </div> */}

                        {/* Allowed Branches dropdown: Only if limitBranches is checked */}
                        {allowedBranch && (
                          <div className="flex sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-2 px-6 text-sm font-medium leading-6 text-gray-900 dark:text-gray-400">
                            <label htmlFor="branches">Allowed Branches</label>
                            <div className="relative w-full">
                              {/* Dropdown toggle */}
                              <button
                                type="button"
                                onClick={() => setShowBranches((prev) => !prev)}
                                className="w-full border rounded-lg px-3 py-1 flex justify-between items-center dark:bg-darkbg dark:text-gray-200"
                              >
                                {allowedUniversityBranches.length ===
                                  allUniversityBranches.length
                                  ? "All Branches Selected"
                                  : `${allowedUniversityBranches.length} Branches Selected`}
                                <span className="ml-2">
                                  {showBranches ? (
                                    <ChevronUpIcon className="h-5" />
                                  ) : (
                                    <ChevronDownIcon className="h-5" />
                                  )}
                                </span>
                              </button>

                              {/* Dropdown menu */}
                              {showBranches && (
                                <div className="absolute z-10 mt-2 w-full max-h-64 overflow-y-auto border rounded bg-white dark:bg-darkbg shadow-lg p-3">
                                  {/* Select All button */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (
                                        allowedUniversityBranches.length ===
                                        allUniversityBranches.length
                                      ) {
                                        setAllowedUniversityBranches([]);
                                      } else {
                                        setAllowedUniversityBranches(
                                          allUniversityBranches
                                        );
                                      }
                                    }}
                                    className="mb-2 text-sm underline"
                                  >
                                    {allowedUniversityBranches.length ===
                                      allUniversityBranches.length
                                      ? "Deselect All"
                                      : "Select All"}
                                  </button>

                                  {/* Branch chips */}
                                  <div className="flex flex-wrap gap-2">
                                    {allUniversityBranches.map((branch) => {
                                      const isSelected =
                                        allowedUniversityBranches.some(
                                          (b) => b.id === branch.id
                                        );
                                      return (
                                        <button
                                          key={branch.id}
                                          type="button"
                                          onClick={() => {
                                            if (isSelected) {
                                              setAllowedUniversityBranches(
                                                (prev) =>
                                                  prev.filter(
                                                    (b) => b.id !== branch.id
                                                  )
                                              );
                                            } else {
                                              setAllowedUniversityBranches(
                                                (prev) => [...prev, branch]
                                              );
                                            }
                                          }}
                                          className={`px-3 py-1 rounded-full border text-sm ${isSelected
                                            ? "bg-entntblue text-white border-entntblue"
                                            : "bg-gray-100 text-gray-700 dark:bg-darkbg dark:text-gray-200"
                                            }`}
                                        >
                                          {branch.name}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {!isEditRole && (
                    <AllWorkflowsDropDown
                      allWorkflows={roleWorkflows}
                      setWorkflows={setRoleWorkflows}
                      selectedWorkFlow={selectedWorkFlow}
                      setSelectedWorkflow={setSelectedWorkFlow}
                    />
                  )}
                  <CreateRoleStatus
                    processes={processes}
                    setProcesses={setProcesses}
                    handleTextEditor={handleTextEditor}
                    setRecruitmentProcessId={setRecruitmentProcessId}
                  />
                  <div className="flex sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                    <label
                      htmlFor="assessment-version"
                      className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-400"
                    >
                      Assessment Submission Version
                    </label>
                    <div className="mt-2 sm:col-span-2 sm:mt-0">
                      <select
                        id="assessment-version"
                        value={selectedAssessmentVersion}
                        onChange={(e) =>
                          setSelectedAssessmentVersion(Number(e.target.value))
                        }
                        className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-entntblue focus:outline-none focus:ring-1 focus:ring-entntblue dark:border-gray-600 dark:bg-darkbg dark:text-gray-200"
                      >
                        {assessmentVersions.map((version) => (
                          <option key={version.id} value={version.id}>
                            {version.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <CreateAssessmentSlots
                    processes={processes}
                    slots={slots}
                    setSlots={setSlots}
                    selectedProcess={selectedProcess}
                    setSelectedProcess={setSelectedProcess}
                  />
                  {textEditorFields.map((field) => (
                    <div
                      key={field.id}
                      className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6"
                      onClick={() => setInputType("")}
                    >
                      <TextEditorFields
                        label={field.label}
                        state={field.state}
                        setTextEditorType={field.setTextEditorType}
                        setShowTextEditor={field.setShowTextEditor}
                      />
                    </div>
                  ))}
                  <RankingType
                    selected={selectedRankType}
                    setSelected={setSelectedRankType}
                    rankTypes={rankTypes}
                  />
                  <CreateProcessAutomation
                    processes={processes}
                    processAutomations={processAutomations}
                    setProcessAutomations={setProcessAutomations}
                    assessments={assessments}
                  />
                  <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 sm:pt-12">
                    <label
                      // htmlFor="openDate"
                      className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 dark:text-gray-400"
                    >
                      Open Date
                    </label>
                    <div className="mt-2 sm:col-span-2 sm:mt-0">
                      <div className="mb-4 flex flex-row items-center gap-5">
                        <div>
                          <DatePicker
                            id="openDate"
                            selected={openDate}
                            onChange={(date: Date | null) => {
                              setOpenDate(date);
                            }}
                            showTimeSelect
                            minDate={new Date()}
                            dateFormat="MMMM d, yyyy h:mm aa"
                            className="w-[21rem] p-1 text-sm rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 pl-10 dark:bg-darkbg dark:text-gray-200"
                          />
                          <CalendarDaysIcon className="w-5 h-5 relative -top-6 left-2 text-gray-500" />
                        </div>
                        <ArrowPathIcon
                          height={18}
                          className="dark:text-gray-200 text-gray-800 cursor-pointer mb-5"
                          onClick={() => {
                            setOpenDate(null);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 sm:pt-12">
                    <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 dark:text-gray-400">
                      Close Date
                    </label>
                    <div className="mt-2 sm:col-span-2 sm:mt-0">
                      <div className="mb-4 flex flex-row items-center text-center gap-5">
                        <div className="">
                          <DatePicker
                            id="closeDate"
                            selected={closeDate}
                            onChange={(date: Date | null) => {
                              setCloseDate(date);
                            }}
                            showTimeSelect
                            minDate={new Date()}
                            dateFormat="MMMM d, yyyy h:mm aa"
                            className="w-[21rem] p-1 text-sm rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 pl-10 dark:bg-darkbg dark:text-gray-200"
                          />
                          <CalendarDaysIcon className="w-5 h-5 relative -top-6 left-2 text-gray-500" />
                        </div>
                        <ArrowPathIcon
                          height={18}
                          className="dark:text-gray-200 text-gray-800 cursor-pointer mb-5"
                          onClick={() => {
                            setCloseDate(null);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {isEditRole ? (
                <div
                  className="mt-6 pb-8 flex items-center justify-end"
                  title={errorMessageToolTip}
                >
                  <button
                    type="button"
                    className={`inline-flex justify-center rounded-md bg-entntblue px-5 py-2 text-sm font-semibold text-white shadow-sm  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-entntblue ${!isFormValid() || !isFormChanged
                      ? "disabled:bg-gray-300 cursor-not-allowed"
                      : "hover:bg-entntorange"
                      }`}
                    onClick={handleUpdateClick}
                    disabled={!isFormValid() || !isFormChanged}
                  >
                    Update
                  </button>
                </div>
              ) : (
                <div
                  className="mt-6 flex items-center justify-end gap-x-6"
                  title={errorMessageToolTip}
                >
                  <button
                    type="button"
                    className={`inline-flex justify-center rounded-md bg-entntblue px-5 py-2 text-sm font-semibold text-white shadow-sm  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-entntblue ${!isFormValid()
                      ? "disabled:bg-gray-300 cursor-not-allowed"
                      : "hover:bg-entntorange"
                      }`}
                    onClick={handleSaveClick}

                    disabled={!isFormValid()}
                  >
                    Create
                  </button>
                </div>
              )}
            </div>
          </div>
        </form>
      ) : (
        <TextEditor
          textEditorType={textEditorType}
          setShowTextEditor={setShowTextEditor}
          setJobDescription={setJobDescription}
          setOfferEmail={setOfferEmail}
          setRejectionEmail={setRejectionEmail}
          setProcesses={setProcesses}
          recruitmentProcessId={recruitmentProcessId}
          defaultStateValue={
            roleId?.roleId && getDefaultStateValue(textEditorType)
          }
          inputType={inputType}
          currentProcess={processes.find((el) => el.id === textEditorType)}
        />
      )}
    </>
  );
}
